import SyllabusTable from '@/components/syllabus-table/SyllabusTable'
import {
	Syllabus,
	SyllabusExternal,
	WebUiSyllabusTile,
} from '@/kontent/content-types'
import { TaxoKeyLearningArea } from '@/kontent/taxonomies'
import { ISelectOption } from '@/legacy-ported/components/base/CustomSelect'
import SearchBar from '@/legacy-ported/components/base/SearchBar'
import type { Mapping, TaxoStageGroupWithLifeSkill } from '@/types'
import { getLinkedItems } from '@/utils'
import { isLifeSkillSyllabus } from '@/utils/syllabus'
import type { IContentItemsContainer } from '@kontent-ai/delivery-sdk'
import { ElementModels } from '@kontent-ai/delivery-sdk'
import clsx from 'clsx'
import { FormGroupSelect } from 'nsw-ds-react'
import { useState } from 'react'

export interface SyllabusListingProps {
	items: WebUiSyllabusTile[]
	linkedItems: IContentItemsContainer
	mappings: Mapping[]
	stageFilterData?: ElementModels.TaxonomyTerm<TaxoStageGroupWithLifeSkill>[]
	keyLearningAreasOptions?: ElementModels.TaxonomyTerm<TaxoKeyLearningArea>[]
	isSortSyllabusByYear?: boolean
}

const searchWithinSyllabus = (
	searchText: string,
	syl: Syllabus | SyllabusExternal,
) => {
	return [syl.elements.title.value, syl.elements.seo_keywords.value]
		.map((str) => str.toLowerCase())
		.some((str) => str.includes(searchText.toLowerCase()))
}

function SyllabusListing({
	items: syllabusTiles,
	linkedItems,
	mappings,
	stageFilterData,
	keyLearningAreasOptions,
	isSortSyllabusByYear = true,
}: SyllabusListingProps) {
	const [searchText, setSearchText] = useState('')
	const [stageFilter, setStageFilter] = useState<
		TaxoStageGroupWithLifeSkill | ''
	>('')
	const [klaFilter, setKlaFilter] = useState<TaxoKeyLearningArea | ''>('')

	const stageFilterOptions: ISelectOption[] = stageFilterData?.map(
		(stage) => {
			return {
				value: stage.codename,
				text: stage.name,
			}
		},
	)

	const klaFilterOptions: ISelectOption[] = keyLearningAreasOptions?.map(
		(kla) => {
			return {
				value: kla.codename,
				text: kla.name,
			}
		},
	)
	const allSyllabusTiles = syllabusTiles
		// Filter by search text
		.filter((syllabusTile) => {
			if (!searchText) return true
			const leftSyllabuses = getLinkedItems(
				syllabusTile.elements.syllabuses_left,
				linkedItems,
			)
			const rightSyllabuses = getLinkedItems(
				syllabusTile.elements.syllabuses_right,
				linkedItems,
			)
			return (
				leftSyllabuses.some((syllabus) => {
					return searchWithinSyllabus(searchText, syllabus)
				}) ||
				rightSyllabuses.some((syllabus) => {
					return searchWithinSyllabus(searchText, syllabus)
				})
			)
		})
		// Filter by stage
		.filter((syllabusTile) => {
			if (!stageFilter) return true

			const stageGroupFilter: TaxoStageGroupWithLifeSkill =
				stageFilter === 'life_skills' ? 'senior' : stageFilter

			const inStageGroup =
				syllabusTile.elements.stages__stage_groups.value.some(
					(stage) => stage.codename === stageGroupFilter,
				)

			if (stageFilter === 'life_skills') {
				const leftSyllabuses = getLinkedItems(
					syllabusTile.elements.syllabuses_left,
					linkedItems,
				)
				const rightSyllabuses = getLinkedItems(
					syllabusTile.elements.syllabuses_right,
					linkedItems,
				)

				return (
					inStageGroup &&
					(leftSyllabuses.some((syllabus) =>
						isLifeSkillSyllabus(syllabus),
					) ||
						rightSyllabuses.some((syllabus) =>
							isLifeSkillSyllabus(syllabus),
						))
				)
			}

			return inStageGroup
		})
		.filter((syllabusTile) => {
			if (!klaFilter) return true

			const syllabusTileKla =
				syllabusTile.elements.key_learning_area.value

			const leftSyllabuses = getLinkedItems(
				syllabusTile.elements.syllabuses_left,
				linkedItems,
			)
			const rightSyllabuses = getLinkedItems(
				syllabusTile.elements.syllabuses_right,
				linkedItems,
			)

			if (syllabusTileKla.length > 0) {
				return syllabusTileKla.some((kla) => kla.codename === klaFilter)
			} else {
				return (
					leftSyllabuses.some((syllabus) => {
						return syllabus.elements.key_learning_area_default.value.some(
							(kla) => kla.codename === klaFilter,
						)
					}) ||
					rightSyllabuses.some((syllabus) => {
						return syllabus.elements.key_learning_area_default.value.some(
							(kla) => kla.codename === klaFilter,
						)
					})
				)
			}
		})
		.sort((a, b) => {
			// Get stage groups
			const aStageGroup =
				a.elements.stages__stage_groups.value[0].codename
			const bStageGroup =
				b.elements.stages__stage_groups.value[0].codename

			// Get titles
			const aSyllabusLeft = getLinkedItems(
				a.elements.syllabuses_left,
				linkedItems,
			)?.[0]
			const bSyllabusLeft = getLinkedItems(
				b.elements.syllabuses_left,
				linkedItems,
			)?.[0]

			const aTitle = (
				a.elements.title_sort.value ||
				aSyllabusLeft?.elements.title.value ||
				''
			).toLowerCase()
			const bTitle = (
				b.elements.title_sort.value ||
				bSyllabusLeft?.elements.title.value ||
				''
			).toLowerCase()

			// Sort by stage group first, then by title
			const stageGroupCompare = aStageGroup.localeCompare(bStageGroup)
			if (stageGroupCompare !== 0) {
				return stageGroupCompare
			}
			return aTitle.localeCompare(bTitle)
		})

	const clearFilters = () => {
		setSearchText('')
		setStageFilter('')
		setKlaFilter('')
	}

	const hasFilterOptions =
		klaFilterOptions?.length > 0 || stageFilterOptions?.length > 0
	const hasAllFilters =
		klaFilterOptions?.length > 0 && stageFilterOptions?.length > 0

	return (
		<div>
			<div className="my-8 flex flex-col lg:flex-row gap-4 lg:gap-6">
				<div
					className={clsx({
						'flex-1': hasAllFilters,
						'flex-[2]': !hasAllFilters,
					})}
				>
					<SearchBar
						initialSearchText={searchText}
						onSearch={(e) => setSearchText(e)}
						searchBarPlaceholder="Search for syllabus"
					/>
				</div>
				<div
					className={clsx({
						'flex flex-1 gap-4 lg:gap-6': hasFilterOptions,
						hidden: !hasFilterOptions,
					})}
				>
					{klaFilterOptions?.length > 0 && (
						<div className="flex-1">
							<FormGroupSelect
								value={klaFilter}
								label="Filter by stage"
								hideLabel
								name="klaFilter"
								placeholder="All key learning areas"
								options={klaFilterOptions}
								onChange={(e) =>
									setKlaFilter(
										e.target.value as
											| TaxoKeyLearningArea
											| '',
									)
								}
							/>
						</div>
					)}
					{stageFilterOptions?.length > 0 && (
						<div className="flex-1">
							<FormGroupSelect
								value={stageFilter}
								label="Filter by stage"
								hideLabel
								name="stageFilter"
								placeholder="All stages of learning"
								options={stageFilterOptions}
								onChange={(e) =>
									setStageFilter(
										e.target.value as
											| TaxoStageGroupWithLifeSkill
											| '',
									)
								}
							/>
						</div>
					)}
				</div>
			</div>
			<SyllabusTable
				items={allSyllabusTiles}
				linkedItems={linkedItems}
				mappings={mappings}
				onClearFilter={clearFilters}
				isFiltered={searchText.length > 0 || stageFilter.length > 0}
				isSortByYear={isSortSyllabusByYear}
			/>
		</div>
	)
}

export default SyllabusListing
